import React, { useState, useEffect } from 'react';
import { Globe, RefreshCw, Unlink, ExternalLink, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { GlassButton, GlassCard } from '../ui';
import {
    connectNetlify, disconnectNetlify, getDeployStatus, triggerDeploy,
    DeployStatus,
} from '../../services/api';

interface Props {
    siteId: string;
    siteName: string;
}

const STATUS_ICON: Record<string, React.ReactNode> = {
    ok:       <CheckCircle size={14} style={{ color: 'var(--success, #22c55e)' }} />,
    failed:   <AlertTriangle size={14} style={{ color: 'var(--danger, #ef4444)' }} />,
    retrying: <RefreshCw size={14} style={{ color: 'var(--warning, #f59e0b)', animation: 'spin 1.5s linear infinite' }} />,
};

const NetlifyConnectPanel: React.FC<Props> = ({ siteId, siteName }) => {
    const [status, setStatus]         = useState<DeployStatus | null>(null);
    const [loading, setLoading]         = useState(true);
    const [connecting, setConnecting]   = useState(false);
    const [deploying, setDeploying]     = useState(false);
    const [error, setError]             = useState<string | null>(null);
    const [success, setSuccess]         = useState<string | null>(null);
    const [offerDeploy, setOfferDeploy] = useState(false);  // show "deploy now?" after connect

    // Connect form state
    const [token, setToken]   = useState('');
    const [netSiteId, setNetSiteId] = useState('');
    const [showForm, setShowForm]   = useState(false);

    useEffect(() => {
        loadStatus();
    }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadStatus = async () => {
        setLoading(true);
        try {
            const s = await getDeployStatus(siteId);
            setStatus(s);
            if (!s.deploy_target) setShowForm(true);
        } catch { /* non-fatal */ }
        finally { setLoading(false); }
    };

    const handleConnect = async () => {
        if (!token.trim() || !netSiteId.trim()) {
            setError('Both API token and Site ID are required');
            return;
        }
        setConnecting(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await connectNetlify(siteId, {
                netlify_api_token: token.trim(),
                netlify_site_id: netSiteId.trim(),
            });
            setSuccess(`Connected to "${res.netlify_site_name}" — deploy to publish your site`);
            setShowForm(false);
            setOfferDeploy(true);
            setToken('');
            setNetSiteId('');
            await loadStatus();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Connection failed');
        } finally { setConnecting(false); }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect Netlify? Automatic deploys will stop.')) return;
        try {
            await disconnectNetlify(siteId);
            setStatus(null);
            setShowForm(true);
            setSuccess(null);
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Disconnect failed');
        }
    };

    const handleDeploy = async () => {
        setDeploying(true);
        setError(null);
        setSuccess(null);
        try {
            await triggerDeploy(siteId);
            setSuccess('Deploy queued — will run within 5 minutes');
            await loadStatus();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Deploy failed');
        } finally { setDeploying(false); }
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '8px 12px',
        background: 'var(--panel-strong)',
        border: '1px solid var(--stroke)',
        borderRadius: 6,
        color: 'var(--text)',
        fontSize: '0.875rem',
        fontFamily: 'inherit',
        outline: 'none',
        boxSizing: 'border-box',
    };

    const labelStyle: React.CSSProperties = {
        fontSize: '0.78rem',
        fontWeight: 700,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 4,
        display: 'block',
    };

    if (loading) {
        return (
            <GlassCard style={{ padding: 16 }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Loading deploy settings…</div>
            </GlassCard>
        );
    }

    const isConnected = !!status?.deploy_target;

    return (
        <GlassCard style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Globe size={18} style={{ color: 'var(--primary-500)' }} />
                <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>Netlify Deploy</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        Automatically publish {siteName} to Netlify when you update blog posts
                    </div>
                </div>
            </div>

            {error && (
                <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, color: 'var(--danger, #dc2626)', fontSize: '0.875rem' }}>
                    {error}
                </div>
            )}
            {success && (
                <div style={{ padding: '8px 12px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 6, color: 'var(--success, #16a34a)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ flex: 1 }}>{success}</span>
                    {offerDeploy && (
                        <GlassButton
                            variant="primary"
                            onClick={async () => { setOfferDeploy(false); await handleDeploy(); }}
                            disabled={deploying}
                            style={{ fontSize: '0.78rem', padding: '4px 12px', flexShrink: 0 }}
                        >
                            {deploying ? 'Queueing…' : 'Deploy now'}
                        </GlassButton>
                    )}
                </div>
            )}

            {isConnected ? (
                <>
                    {/* Status row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--panel-strong)', borderRadius: 8, border: '1px solid var(--stroke)' }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>
                                Site ID: {status!.deploy_target_id}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                                {status!.deploy_status ? STATUS_ICON[status!.deploy_status] : null}
                                {status!.deploy_status === 'ok' && 'Last deploy succeeded'}
                                {status!.deploy_status === 'failed' && `Failed: ${status!.last_redeploy_error || 'unknown error'}`}
                                {status!.deploy_status === 'retrying' && 'Retrying…'}
                                {!status!.deploy_status && 'Not yet deployed'}
                                {status!.pending_redeploy_at && !status!.deploy_status?.includes('retry') && (
                                    <span style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        <Clock size={11} /> Deploy pending
                                    </span>
                                )}
                            </div>
                        </div>
                        {status!.last_redeploy_attempt_at && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
                                {new Date(status!.last_redeploy_attempt_at).toLocaleDateString()}
                            </div>
                        )}
                    </div>

                    {status!.deploy_url && (
                        <a
                            href={status!.deploy_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '0.8rem', color: 'var(--primary-500)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                            <ExternalLink size={12} /> {status!.deploy_url}
                        </a>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <GlassButton
                            variant="primary"
                            onClick={handleDeploy}
                            disabled={deploying}
                            style={{ flex: 1 }}
                        >
                            {deploying
                                ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Queueing…</>
                                : <><ExternalLink size={13} /> Deploy now</>
                            }
                        </GlassButton>
                        <GlassButton variant="secondary" onClick={() => setShowForm(v => !v)}>
                            Reconfigure
                        </GlassButton>
                        <GlassButton variant="secondary" onClick={handleDisconnect}>
                            <Unlink size={13} />
                        </GlassButton>
                    </div>

                    {status!.deploy_status === 'failed' && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--danger, #dc2626)' }}>
                            Auto-deploy stopped after {status!.deploy_failure_count} failures. Click "Deploy now" to retry.
                        </div>
                    )}
                </>
            ) : null}

            {/* Connect / Reconfigure form */}
            {(showForm || !isConnected) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, borderTop: isConnected ? '1px solid var(--stroke)' : 'none', paddingTop: isConnected ? 12 : 0 }}>
                    {isConnected && <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>Reconfigure</div>}
                    <div>
                        <label style={labelStyle}>Netlify API Token</label>
                        <input
                            type="password"
                            style={inputStyle}
                            value={token}
                            onChange={e => setToken(e.target.value)}
                            placeholder="nfp_…"
                            autoComplete="off"
                        />
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                            Personal access token from Netlify → User settings → Applications
                        </div>
                    </div>
                    <div>
                        <label style={labelStyle}>Netlify Site ID</label>
                        <input
                            style={inputStyle}
                            value={netSiteId}
                            onChange={e => setNetSiteId(e.target.value)}
                            placeholder="your-site.netlify.app or site UUID"
                        />
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                            Found in Netlify → Site settings → General → Site ID
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <GlassButton variant="primary" onClick={handleConnect} disabled={connecting} style={{ flex: 1 }}>
                            {connecting ? 'Connecting…' : 'Connect Netlify'}
                        </GlassButton>
                        {isConnected && (
                            <GlassButton variant="secondary" onClick={() => setShowForm(false)}>Cancel</GlassButton>
                        )}
                    </div>
                </div>
            )}
        </GlassCard>
    );
};

export default NetlifyConnectPanel;
